// Slice 30 — PostgreSQL rename emitter.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 30.
//
// Emits dialect-native ALTER syntax for body rewrites + table/view rename.
// The planner (renamePlanner.ts) already routes column rename schema-ALTERs
// through `DdlStructuralEdit.applyDdlEdit`; this file owns table/view rename
// + object body rewrites only.
//
// Body shapes (as returned by the introspection feed in `pgsqlObjectBodyFetcher.ts`):
//   - view → bare SELECT body (`pg_views.definition`).
//   - inline-tvf / scalar-function → full `CREATE OR REPLACE FUNCTION …`
//     statement (`pg_get_functiondef(p.oid)` returns CREATE OR REPLACE form
//     on PG 12+; older shapes are normalised by `ensureCreateOrReplace`).
//   - default-constraint → bare expression (`pg_get_expr(d.adbin, d.adrelid)`),
//     synthesized object name is `<table>.<column>`.
//   - check-constraint → `CHECK (…)` definition (`pg_get_constraintdef`).
//
// DEFAULT constraints carry their parent table inside the synthesized
// `<table>.<column>` name, so we emit static `ALTER TABLE … ALTER COLUMN …
// SET DEFAULT` SQL. CHECK constraints carry only `(schema, conname)` — the
// parent table is unknown until execution time, so we wrap the DROP + ADD
// pair in a `DO $$ … $$` block that resolves `pg_constraint.conrelid` at
// runtime, mirroring the MSSQL emitter's dynamic-SQL pattern.

import type { ObjectKind, QualifiedName } from '../../types';

import type {
  DialectRenameEmitter,
  EmitObjectBodyAlterInput,
} from './types';

const DEFAULT_SCHEMA = 'public';

function quoteIdent(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}

function quoteSqlLiteral(text: string): string {
  return text.replace(/'/g, "''");
}

function schemaOf(qn: QualifiedName): string {
  return qn.schema && qn.schema.length > 0 ? qn.schema : DEFAULT_SCHEMA;
}

function quotedQualified(qn: QualifiedName): string {
  return `${quoteIdent(schemaOf(qn))}.${quoteIdent(qn.name)}`;
}

/** Substitute the leading `CREATE` (or `CREATE OR REPLACE`) in a CREATE-form
 *  module body with `CREATE OR REPLACE`. Whitespace + casing of the rest is
 *  preserved byte-for-byte so any modifiers (LANGUAGE, args, returns,
 *  SECURITY DEFINER, etc.) survive intact. */
function ensureCreateOrReplace(body: string): string {
  return body.replace(/^\s*create\s+(or\s+replace\s+)?/i, 'CREATE OR REPLACE ');
}

function emitViewBodyAlter(qn: QualifiedName, newBody: string): string {
  // `pg_views.definition` returns the bare SELECT — re-wrap in CREATE OR
  // REPLACE VIEW. Trailing `;` (PG sometimes appends one) is stripped so the
  // result has no double terminator.
  const inner = newBody.replace(/;\s*$/, '').trim();
  return `CREATE OR REPLACE VIEW ${quotedQualified(qn)} AS ${inner}`;
}

function emitFunctionBodyAlter(newBody: string): string {
  // pg_get_functiondef returns `CREATE OR REPLACE FUNCTION …` on PG 12+;
  // hand-written / older bodies might be plain `CREATE FUNCTION`. Normalise.
  return ensureCreateOrReplace(newBody);
}

function emitTriggerBodyAlter(newBody: string): string {
  // PG 14+ supports `CREATE OR REPLACE TRIGGER`; older callers must DROP +
  // CREATE. The introspection feed does not yet fetch trigger bodies (Slice
  // 17 only enumerates view + functions + constraints), so this branch is
  // exercised only when a future slice routes trigger bodies through the
  // planner. We emit the OR REPLACE form; pre-14 dispatch is the future
  // slice's responsibility.
  return ensureCreateOrReplace(newBody);
}

function emitDefaultConstraintAlter(
  qn: QualifiedName,
  newExpr: string,
): string {
  // Synthesized object name is `<table>.<column>` — split on the LAST dot so
  // a dotted column name like `a.b.c` splits as table=`a.b` column=`c`. The
  // introspection feed never produces dotted columns (`pg_attribute.attname`
  // is a bare identifier) but the dot-from-the-right rule is robust.
  const dot = qn.name.lastIndexOf('.');
  if (dot < 0) {
    return `-- TODO(rename emit): default-constraint name "${qn.name}" missing the synthesized "<table>.<column>" shape; cannot emit ALTER`;
  }
  const table = qn.name.slice(0, dot);
  const column = qn.name.slice(dot + 1);
  return `ALTER TABLE ${quoteIdent(schemaOf(qn))}.${quoteIdent(table)} ALTER COLUMN ${quoteIdent(column)} SET DEFAULT ${newExpr.trim()}`;
}

function emitCheckConstraintAlter(
  qn: QualifiedName,
  newBody: string,
): string {
  // `pg_get_constraintdef` returns `CHECK (…)` (and possibly trailing
  // `NO INHERIT`). PG accepts that whole shape as the `<constraint_def>` part
  // of `ALTER TABLE … ADD CONSTRAINT name <constraint_def>`, so we use it as
  // the suffix verbatim and wrap the DROP + ADD pair in a DO block that
  // resolves the parent table at runtime via `pg_constraint`.
  const schema = schemaOf(qn);
  const sLit = quoteSqlLiteral(schema);
  const nLit = quoteSqlLiteral(qn.name);
  const quotedName = quoteIdent(qn.name);
  // Inside the DO block the DROP/ADD are EXECUTEd as text — we replace the
  // outer `'` delimiters by doubling every `'` inside `quotedName` and
  // `newBody`.
  const newBodyEscaped = quoteSqlLiteral(newBody.trim());
  const quotedNameEscaped = quoteSqlLiteral(quotedName);
  return [
    'DO $jamsql_rename$',
    'DECLARE v_table TEXT;',
    'BEGIN',
    '  SELECT format(\'%I.%I\', n.nspname, c.relname) INTO v_table',
    '    FROM pg_catalog.pg_constraint con',
    '    JOIN pg_catalog.pg_class c ON c.oid = con.conrelid',
    '    JOIN pg_catalog.pg_namespace n ON n.oid = con.connamespace',
    `    WHERE con.conname = '${nLit}' AND n.nspname = '${sLit}' AND con.contype = 'c';`,
    `  EXECUTE 'ALTER TABLE ' || v_table || ' DROP CONSTRAINT ${quotedNameEscaped}';`,
    `  EXECUTE 'ALTER TABLE ' || v_table || ' ADD CONSTRAINT ${quotedNameEscaped} ${newBodyEscaped}';`,
    'END',
    '$jamsql_rename$;',
  ].join('\n');
}

function isModuleKind(kind: ObjectKind): boolean {
  return (
    kind === 'view' ||
    kind === 'materialized-view' ||
    kind === 'inline-tvf' ||
    kind === 'scalar-function' ||
    kind === 'procedure' ||
    kind === 'trigger'
  );
}

export function emitObjectBodyAlter(input: EmitObjectBodyAlterInput): string {
  const { objectKind, objectName, newBody } = input;
  if (objectKind === 'check-constraint') {
    return emitCheckConstraintAlter(objectName, newBody);
  }
  if (objectKind === 'default-constraint') {
    return emitDefaultConstraintAlter(objectName, newBody);
  }
  if (objectKind === 'view' || objectKind === 'materialized-view') {
    return emitViewBodyAlter(objectName, newBody);
  }
  if (objectKind === 'trigger') {
    return emitTriggerBodyAlter(newBody);
  }
  if (isModuleKind(objectKind)) {
    return emitFunctionBodyAlter(newBody);
  }
  return emitFunctionBodyAlter(newBody);
}

export type SchemaRenameInput = {
  readonly kind: 'table' | 'view';
  readonly from: QualifiedName;
  readonly to: QualifiedName;
};

export function emitTableOrViewSchemaAlter(input: SchemaRenameInput): string {
  const head = input.kind === 'table' ? 'ALTER TABLE' : 'ALTER VIEW';
  return `${head} ${quotedQualified(input.from)} RENAME TO ${quoteIdent(input.to.name)}`;
}

export type EmitUndoAlterInput =
  | {
      readonly kind: 'body';
      readonly objectKind: ObjectKind;
      readonly objectName: QualifiedName;
      readonly originalBody: string;
    }
  | {
      readonly kind: 'schema';
      readonly schemaRename: SchemaRenameInput;
    };

export function emitUndoAlter(input: EmitUndoAlterInput): string {
  if (input.kind === 'body') {
    return emitObjectBodyAlter({
      objectKind: input.objectKind,
      objectName: input.objectName,
      newBody: input.originalBody,
    });
  }
  return emitTableOrViewSchemaAlter({
    kind: input.schemaRename.kind,
    from: input.schemaRename.to,
    to: input.schemaRename.from,
  });
}

export const postgresRenameEmitter: DialectRenameEmitter = {
  emitObjectBodyAlter,
  emitTableOrViewSchemaAlter,
};
